import { Grid, Paper, Typography, IconButton, Tooltip, List, ListItem, Button } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { DataGrid, GridColDef, gridClasses } from '@mui/x-data-grid';
import b64toBlob from 'b64-to-blob';
import { useEffect, useState, useRef } from 'react';
import { useAppDispatch } from '../../../../store/store';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../reducers';
import { parentUrl } from '../../../../utils/constants';
import { localDateFormat } from '../../../../utils/timeFormat';
import WidgetLoading from '../../Shared/WidgetLoading';
import Authorization from '../../../../utils/Authorization';
import { fetchEducationSessionReminder, exportEducationSessionReminder } from '../../../Widgets/WidgetService';
import TeamDropdown from '../../../Shared/TeamDropdown';
import { fetchTeams } from '../../../../store/careTeams.slice';

const EducationSessionReminder = (props) => {
    const dispatch = useAppDispatch();
    const authData = new Authorization();
    const { careTeams: teams } = useSelector((state: RootState) => state.careTeams);
    const [sessions, setSessions] = useState([]);
    const [isFetched, setIsFetched] = useState<boolean>(false);
    const [show, setShow] = useState(false);
    const [selectedTeam, setSelectedTeam] = useState(authData.getDefaultTeamId('All'));
    const [showInActiveTeams, setShowInActiveTeams] = useState(false);
    const [pageSize, setPageSize] = useState(15);
    const [page, setPage] = useState(0);
    const ref = useRef(null);
    const [downLoading, setDownLoading] = useState<boolean>(false);

    useEffect(() => {
        dispatch(fetchTeams(true));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const isOlderThan60Days = (row) => {
        const isOlder = new Date(row.CreationTime) < new Date(new Date().setDate(new Date().getDate() - 60));
        const isInProgress = row.Status === 'Plan InProgress';
        return isOlder && isInProgress;
    };

    const returnClassName = (row) => {
        return isOlderThan60Days(row) ? 'is-late' : 'is-in-progress';
    };

    const handleClick = (event) => {
        setShow(!show);
    };

    useEffect(() => {
        const changeFilter = async () => {
            setIsFetched(false);
            const response = await fetchEducationSessionReminder(selectedTeam, showInActiveTeams);
            setSessions(response);
            setIsFetched(true);
        };
        changeFilter();
    }, [selectedTeam, showInActiveTeams]);

    const columns: GridColDef[] = [
        {
            field: 'MemberName',
            headerName: 'Member',
            minWidth: 140,
        },
        {
            field: 'Type',
            headerName: 'Type',
            minWidth: 140,
        },
        {
            field: 'Status',
            headerName: 'Status',
            minWidth: 140,
        },
        {
            field: 'Audience',
            headerName: 'Audience',
            minWidth: 140,
        },
        {
            field: 'LevelOfTeachback',
            headerName: 'Level of Teach Back',
            minWidth: 165,
        },
        {
            field: 'LevelOfUnderstanding',
            headerName: 'Level of Understanding',
            minWidth: 165,
        },
        {
            field: 'CreationTime',
            headerName: 'Created',
            minWidth: 140,
            valueGetter: (value, row) => `${localDateFormat(new Date(row.CreationTime))}`,
        },
        {
            field: 'CreatedBy',
            headerName: 'Provider Name',
            minWidth: 165,
            valueGetter: (value, row) => `${row.CreatedBy?.Name}`,
        },
    ];

    const exportEducationSessionReminders = async () => {
        setDownLoading(true);
        const response = await exportEducationSessionReminder(selectedTeam, showInActiveTeams);
        let blob = b64toBlob(response.base64String, 'text/csv');
        let url = window.URL.createObjectURL(blob);
        let link = document.createElement('a');
        link.href = url;
        var date = new Date();
        var mm = date.getMonth() + 1; // getMonth() is zero-based
        var dd = date.getDate();
        var dateInfo = [(mm > 9 ? '' : '0') + mm, (dd > 9 ? '' : '0') + dd, date.getFullYear()].join('');
        link.setAttribute('download', `EducationSessionReminder - ${dateInfo}.xlsx`);
        link.click();
        link.remove();
        setDownLoading(false);
    };

    return (
        <Grid size={{ xs: 12, lg: 6 }}>
            <Paper
                className="smartdashboard-widget"
                elevation={5}
                sx={{ height: 'calc(100vh - 140px)', maxHeight: '650px', display: 'flex', flexDirection: 'column', overflow: 'auto' }}
            >
                <Grid container spacing={2} paddingLeft={2} paddingRight={2}>
                    <Grid size={12} textAlign="center">
                        <Typography color={'primary'} variant="h6" textAlign={'center'} ref={ref}>
                            Education Session Reminder
                            <Tooltip
                                open={show}
                                title={
                                    <div>
                                        <Typography variant="caption">In Progress Member Educations colored by Status</Typography>
                                        <List>
                                            <ListItem>
                                                <Typography variant="caption" color={'salmon'}>
                                                    Red: Older than 60 days
                                                </Typography>
                                            </ListItem>
                                            <ListItem>
                                                <Typography variant="caption" color={'lightgreen'}>
                                                    Green: Not older than 60 days
                                                </Typography>
                                            </ListItem>
                                        </List>
                                    </div>
                                }
                                PopperProps={{ style: { zIndex: 1000 } }}
                            >
                                <IconButton onClick={handleClick}>
                                    <InfoIcon sx={{ width: '18px', height: '18px' }} />
                                </IconButton>
                            </Tooltip>
                        </Typography>
                    </Grid>
                    <Grid size={4}>
                        <TeamDropdown
                            teams={teams}
                            selectedTeam={selectedTeam}
                            onChange={(id) => setSelectedTeam(id)}
                            showInactive={showInActiveTeams}
                            onToggleInactive={setShowInActiveTeams}
                        />
                    </Grid>
                    <Grid size={6}></Grid>
                    <Grid size={2} maxHeight="55px">
                        <Button
                            style={{ minWidth: '120px', marginLeft: 'auto' }}
                            onClick={() => exportEducationSessionReminders()}
                            variant="contained"
                            color="success"
                            disabled={downLoading || sessions?.length === 0}
                        >
                            {downLoading ? 'Exporting..' : 'Export'}
                        </Button>
                    </Grid>
                    <Grid size={12} marginTop={1}>
                        <div style={{ width: '100%', height: 'Calc(71vh - 135px)', position: 'relative' }}>
                            {!isFetched && <WidgetLoading />}
                            <DataGrid
                                onCellClick={(params: any) => {
                                    window.open(`${parentUrl}provider/members-list/member-chart/${params.row.MemberId}/education`, '_blank');
                                }}
                                rows={sessions}
                                columns={columns}
                                paginationModel={{ pageSize: pageSize, page: page }}
                                pageSizeOptions={[5]}
                                onPaginationModelChange={(model) => {
                                    setPageSize(model.pageSize);
                                    setPage(model.page);
                                }}
                                getRowId={(row) => row.Id}
                                getRowHeight={() => 'auto'}
                                getRowClassName={(params) => returnClassName(params.row)}
                                sx={{
                                    [`& .${gridClasses.cell}`]: {
                                        py: 1,
                                    },
                                    '& .is-late': {
                                        color: 'red',
                                    },
                                    '& .is-in-progress': {
                                        color: 'green',
                                    },
                                }}
                            />
                        </div>
                    </Grid>
                </Grid>
            </Paper>
        </Grid>
    );
};

export default EducationSessionReminder;
